import { useLazyQuery, useMutation, useQuery } from '@apollo/client';
import { DOWNLOAD_CONTENT, GET_ALLCONTENT, GET_BRANCH_CONTENT, GET_SUBJECT_CONTENT } from './queries';
import { CREATE_CONTENT, CREATE_EMPTY_FOLDER, EDIT_FOLDER_CONTENT, REMOVE_CONTENT, REMOVE_FOLDER_CONTENT, UPDATE_CONTENT } from './mutations';
import { type IContent } from '../../types/content';
import { useBranch } from '../../hooks/useBranch';
import { useUser } from '../../hooks/useUser';
import { type ISubject } from '../../types/subject';
import { type ITopic } from '../../types/topic';

interface GetContentResponse {
  getAllContent: IContent[]
  getSubjectContent: IContent[]
}
export const useGetContent = () => {
  const { data, loading, refetch } = useQuery<GetContentResponse>(GET_ALLCONTENT, { fetchPolicy: 'network-only' });

  return {
    contents: data?.getAllContent,
    loading,
    refetch
  };
};

export const useGetSubjectContent = () => {
  const [getContent, { loading, data }] = useLazyQuery<GetContentResponse>(GET_SUBJECT_CONTENT, { fetchPolicy: 'network-only' });

  return {
    getContent,
    contents: data?.getSubjectContent,
    loading
  };
};

export const useGetBranchContent = (selectedBranch?: string) => {
  const { branch } = useBranch();
  const { user } = useUser();
  const { data, loading, refetch } = useQuery(GET_BRANCH_CONTENT, {
    variables: {
      input: { branch: branch?._id || selectedBranch, user: user?._id }
    },
    skip: !(selectedBranch || branch),
    fetchPolicy: 'network-only'
  });

  const folders = (data?.getBranchContent as IContent[] || []).reduce((acc: string[], item) => {
    if (!acc) {
      acc = [];
    }
    if (item.folder && !acc.includes(item.folder)) {
      acc.push(item.folder);
    }

    return acc;
  }, []);

  const grouped = (data?.getBranchContent as IContent[] || []).reduce((acc: any, item) => {
    const subject = item.subject as ISubject;
    const topic = item.topic as ITopic;

    if (!acc[subject.name]) {
      acc[subject.name] = {};
    }

    if (!acc[subject.name][`${topic.name}***${topic._id}`]) {
      acc[subject.name][`${topic.name}***${topic._id}`] = [];
    }

    acc[subject.name][`${topic.name}***${topic._id}`].push(item);

    return acc;
  }, {});

  const sortedObj = Object.keys(grouped)
    .sort()
    .reduce((acc: any, key) => {
      acc[key] = grouped[key];
      return acc;
    }, {});

  return {
    grouped: sortedObj,
    contents: data?.getBranchContent as IContent[],
    loading,
    refetch,
    folders
  };
};

export const useCreateContent = () => {
  const [action, { loading }] = useMutation(CREATE_CONTENT);

  const createContent = async (input: IContent) => {
    await action({ variables: { input: { ...input } } });
  };

  return { createContent, loading };
};

export const useCreateEmptyFolder = () => {
  const [action, { loading }] = useMutation(CREATE_EMPTY_FOLDER);

  const createEmptyFolder = async (input: IContent) => {
    await action({ variables: { input: { ...input } } });
  };

  return { createEmptyFolder, loading };
};

export const useUpdateContent = () => {
  const [action, { loading }] = useMutation(UPDATE_CONTENT);

  const updateContent = async (input: IContent) => {
    await action({ variables: { input: { ...input } } });
  };

  return { updateContent, loading };
};

export const useRemoveContent = () => {
  const [action, { loading }] = useMutation(REMOVE_CONTENT);

  const removeContent = async (input: string) => {
    await action({ variables: { input } });
  };

  return { removeContent, loading };
};

interface EditFolderNameInput {
  topic: string
  oldName: string
  newName: string
}

export const useEditFolderContent = () => {
  const [action, { loading }] = useMutation(EDIT_FOLDER_CONTENT);

  const editFolder = async (input: EditFolderNameInput) => {
    await action({ variables: { input } });
  };

  return { editFolder, loading };
};

interface RemoveFolderNameInput {
  topic: string
  folder: string
}

export const useRemoveFolderContent = () => {
  const [action, { loading }] = useMutation(REMOVE_FOLDER_CONTENT);

  const removeFolder = async (input: RemoveFolderNameInput) => {
    await action({ variables: { input } });
  };

  return { removeFolder, loading };
};

export const useDownloadContent = () => {
  const user = useUser();
  const [action, { loading }] = useLazyQuery(DOWNLOAD_CONTENT);

  const downloadContent = async (input: string) => {
    const response = await action({
      variables: {
        input: {
          contentId: input,
          userId: user?.user?._id
        }
      }
    });
    const data = response.data?.downloadContentWithWatermark;

    return data;
  };

  return { downloadContent, loading };
};
